import React, { useCallback, useEffect, useRef, useState } from 'react';
import MathText from './MathText';

// A passage renderer that lets the reader highlight selected text (yellow / orange) with a small
// floating toolbar shown only while a selection exists inside it. Highlights are stored as character
// ranges over `text` ({start, end, color}) and reported via onChange, so the parent can persist them
// per passage for the section/module/quiz. Selecting text elsewhere (questions/options) never triggers
// this, since the toolbar is scoped to this component's own container.
//
// LaTeX guard: passages containing `$` (math) fall back to plain MathText with highlighting disabled —
// character-offset splitting would break math tokens. Reading passages are plain prose.
const YELLOW = '#fde047';
const ORANGE = '#fdba74';

// Character offset of (node, offset) within `container`, walking text nodes in document order.
const offsetWithin = (container, node, nodeOffset) => {
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null);
    let total = 0;
    let n = walker.nextNode();
    while (n) {
        if (n === node) return total + nodeOffset;
        total += n.textContent.length;
        n = walker.nextNode();
    }
    return null;
};

// Split `text` into segments carrying an optional highlight color (non-overlapping, clipped).
const buildSegments = (text, highlights) => {
    const hs = [...(highlights || [])].filter(h => h.end > h.start).sort((a, b) => a.start - b.start);
    const segs = [];
    let i = 0;
    for (const h of hs) {
        const s = Math.max(h.start, i);
        const e = Math.min(h.end, text.length);
        if (s > i) segs.push({ text: text.slice(i, s), color: null });
        if (e > s) segs.push({ text: text.slice(s, e), color: h.color });
        i = Math.max(i, e);
    }
    if (i < text.length) segs.push({ text: text.slice(i), color: null });
    return segs;
};

const HighlightableText = ({ text, highlights = [], onChange, className, style }) => {
    const containerRef = useRef(null);
    const [toolbar, setToolbar] = useState(null); // { x, y, start, end }
    const hasMath = typeof text === 'string' && text.includes('$');

    const closeToolbar = useCallback(() => setToolbar(null), []);

    // Hide the toolbar on scroll / outside interaction.
    useEffect(() => {
        if (!toolbar) return undefined;
        const onScroll = () => setToolbar(null);
        window.addEventListener('scroll', onScroll, true);
        return () => window.removeEventListener('scroll', onScroll, true);
    }, [toolbar]);

    const handleSelectionEnd = useCallback(() => {
        if (hasMath) return;
        const sel = window.getSelection();
        if (!sel || sel.isCollapsed || sel.rangeCount === 0) { setToolbar(null); return; }
        const range = sel.getRangeAt(0);
        const container = containerRef.current;
        if (!container || !container.contains(range.startContainer) || !container.contains(range.endContainer)) {
            setToolbar(null);
            return;
        }
        const a = offsetWithin(container, range.startContainer, range.startOffset);
        const b = offsetWithin(container, range.endContainer, range.endOffset);
        if (a == null || b == null || a === b) { setToolbar(null); return; }
        const start = Math.min(a, b);
        const end = Math.max(a, b);
        const rect = range.getBoundingClientRect();
        setToolbar({ x: rect.left + rect.width / 2, y: rect.top, start, end });
    }, [hasMath]);

    const addHighlight = (color) => {
        if (!toolbar || !onChange) return;
        onChange([...(highlights || []), { start: toolbar.start, end: toolbar.end, color }]);
        window.getSelection()?.removeAllRanges();
        setToolbar(null);
    };

    const removeInSelection = () => {
        if (!toolbar || !onChange) return;
        onChange((highlights || []).filter(h => !(h.start < toolbar.end && h.end > toolbar.start)));
        window.getSelection()?.removeAllRanges();
        setToolbar(null);
    };

    // Click a highlight to remove it.
    const removeAt = (offset) => {
        if (!onChange) return;
        onChange((highlights || []).filter(h => !(offset >= h.start && offset < h.end)));
    };

    if (hasMath) {
        return <div className={className} style={style}><MathText text={text} /></div>;
    }

    const segments = buildSegments(text || '', highlights);
    let cursor = 0;

    return (
        <div ref={containerRef} className={className} style={{ whiteSpace: 'pre-wrap', ...(style || {}) }} onMouseUp={handleSelectionEnd} onTouchEnd={handleSelectionEnd}>
            {segments.map((seg, idx) => {
                const at = cursor;
                cursor += seg.text.length;
                if (!seg.color) return <React.Fragment key={idx}>{seg.text}</React.Fragment>;
                const cls = seg.color === ORANGE ? 'kp-hl kp-hl-orange' : 'kp-hl kp-hl-yellow';
                return (
                    <mark key={idx} className={cls} title="Click to remove highlight" style={{ cursor: 'pointer' }} onClick={() => removeAt(at)}>
                        {seg.text}
                    </mark>
                );
            })}
            {toolbar && (
                <div
                    style={{
                        position: 'fixed', left: toolbar.x, top: toolbar.y - 44, transform: 'translateX(-50%)',
                        zIndex: 2000, display: 'flex', gap: '0.35rem', alignItems: 'center',
                        background: '#111827', padding: '0.3rem 0.4rem', borderRadius: '0.5rem',
                        boxShadow: '0 6px 16px -4px rgba(0,0,0,0.4)',
                    }}
                    onMouseDown={(e) => e.preventDefault()} // keep the text selection while clicking
                >
                    <button onClick={() => addHighlight(YELLOW)} title="Highlight yellow"
                        style={{ width: 22, height: 22, borderRadius: '50%', border: '2px solid #fff', background: YELLOW, cursor: 'pointer' }} />
                    <button onClick={() => addHighlight(ORANGE)} title="Highlight orange"
                        style={{ width: 22, height: 22, borderRadius: '50%', border: '2px solid #fff', background: ORANGE, cursor: 'pointer' }} />
                    <button onClick={removeInSelection} title="Remove highlight"
                        style={{ width: 22, height: 22, borderRadius: '50%', border: '2px solid #fff', background: '#e5e7eb', color: '#111827', cursor: 'pointer', fontWeight: 700, lineHeight: 1 }}>×</button>
                </div>
            )}
        </div>
    );
};

export default HighlightableText;
